import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { ShowDialogProps } from '../../../show-dialog'
import { useZodForm } from '../../../hooks/zod-form'
import { invalidateGlobalDocument, useUpdateGlobalDocument } from '../../../api/global-documents'
import { AppForm, FormCheckbox, FormSelectField, FormSubmitButton, FormTextField } from '../../../components/FormCore'
import { toast } from 'react-toastify'
import { CancelButton } from '../../../components/CancelButton'
import { IDocumentCategory } from '../../../generated-types'
import { invalidateDocumentCategoryCache, useAdminGetDocumentCategories } from '../../../api/document-category'
import { useEffect } from 'react'
import { invalidateDocument } from '../../../api/document'
import { useGetCustomerAndAdminDocumentCategories } from 'src/api/customer-document-category'
import { APIResultCount } from 'src/constant'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { invalidateCustomerCache } from 'src/api/customer-admin'

export interface GlobalDocumentEditDialogProps extends ShowDialogProps {
  isSuperAdmin?: boolean
  documentId: EntityId
  initialValues: any
  customerId?: EntityId
}

export const GlobalDocumentEditDialog: React.FC<GlobalDocumentEditDialogProps> = ({
  pop,
  isSuperAdmin = false,
  documentId,
  initialValues,
  customerId,
}) => {
  const { t } = useTranslation(['global-documents', 'common'])
  const { data: documentTypes } = useAdminGetDocumentCategories()
  const customerDocumentTypes = useGetCustomerAndAdminDocumentCategories(customerId || 0, 0, APIResultCount.Max, {
    enabled: !!customerId,
  })

  const schema = z.object({
    title: z.string().nonempty(t('titleIsRequired', { ns: 'common' })),
    notes: z.string().optional(),
    documentCategoryId: z
      .object({
        id: z.number(),
      })
      .transform(value => value.id),
    isActive: z.boolean().optional(),
    customerId: z.number().optional(),
    isReadApprovalRequired: z.boolean(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      documentCategoryId: (() => {
        let foundCategory = documentTypes?.items?.find(x => x.id === initialValues.documentCategoryId)

        if (!foundCategory && customerId && customerDocumentTypes.data?.items) {
          foundCategory = customerDocumentTypes.data.items.find(x => x.id === initialValues.documentCategoryId)
        }

        return foundCategory
      })(),
    },
  })

  useEffect(() => {
    let foundCategory = documentTypes?.items?.find(x => x.id === initialValues.documentCategoryId)

    if (!foundCategory && customerId && customerDocumentTypes.data?.items) {
      foundCategory = customerDocumentTypes.data.items.find(x => x.id === initialValues.documentCategoryId)
    }

    form.reset({
      ...initialValues,
      documentCategoryId: foundCategory,
    })
  }, [documentTypes, customerDocumentTypes.data, form, initialValues, customerId])

  const mutation = useUpdateGlobalDocument(documentId)

  const onSubmit = async form => {
    form.id = documentId
    form.customerId = customerId ? Number(customerId) : undefined
    await mutation.mutateAsync(form).then(
      () => {
        invalidateGlobalDocument.getDocuments()
        if (customerId) {
          invalidateDocumentCategoryCache.getDocumentCategoriesWithDocumentCountByKey()
          invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
          invalidateDocument.getDocuments(customerId)
          invalidateCustomerCache.getDocumentReadStatus(customerId, documentId)
          invalidateCustomerCache.getUserDocumentVersionReadStatus(customerId, documentId)
          invalidateDocument.getDocumentsForApproval(customerId)
        }
        toast.success(t('successfullyUpdatedDocument', { ns: 'global-documents' }))
        pop()
      },
      () => {
        toast.error(t('updatingError', { ns: 'common' }))
      },
    )
  }

  return (
    <AppForm form={form} onSubmit={onSubmit}>
      <FormTextField name="title" label={t('title', { ns: 'common' })} fullWidth />
      <FormTextField name="notes" label={t('description', { ns: 'common' })} fullWidth multiline rows={4} />
      <FormSelectField
        name="documentCategoryId"
        data={
          customerId
            ? (customerDocumentTypes.data?.items as IDocumentCategory[]) || []
            : (documentTypes?.items as IDocumentCategory[])
        }
        label={t('category', { ns: 'common' })}
        getOptionLabel={option => option.categoryName}
        renderOption={(props: any, option) => (
          <Box {...props} key={option.id}>
            {option.categoryName}
          </Box>
        )}
        isOptionEqualToValue={(o, v) => o.id === v.id}
      />
      <FormCheckbox name="isActive" label={t('isActive', { ns: 'common' })} defaultValue={true} />
      {!isSuperAdmin && (
        <FormCheckbox
          name="isReadApprovalRequired"
          label={t('isReadApprovalRequired', { ns: 'document' })}
          defaultValue={true}
        />
      )}
      <Box className="flex justify-end gap-4 pt-4">
        <CancelButton onClick={pop}>{t('cancel', { ns: 'common' })}</CancelButton>
        <FormSubmitButton createText={t('update', { ns: 'common' })} />
      </Box>
    </AppForm>
  )
}
